import type { BillingPreviewUI } from "@/domain/billing/billing-preview.model";
import type { OrderDetailsUI } from "@/domain/order/order-details.model";
import { evaluateReorderPipeline } from "@/domain/reorder/reorder.pipeline";
import {
  buildReorderFailureAnalyticsPayload,
  deriveCatalogFetchFailurePresentation,
  shouldSurfaceReorderReviewBanner,
} from "@/domain/reorder/reorder.presentation";
import { setReorderCartNotice } from "@/domain/reorder/reorder-notice";
import type {
  ReorderBestEffortSummary,
  ReorderPipelineFailureCode,
  ReorderPresentation,
} from "@/domain/reorder/reorder.types";
import type { Discount } from "@/models/discount";
import type { MenuItemInterface } from "@/models/menu-item";
import type { CartLine } from "@/shared/cart/model.types";
import {
  mergeMenuItemCountsFromCartLines,
  synchronizeCatalogSnapshotAuthority,
  type CatalogSnapshotAuthority,
} from "@/services/catalog-snapshot-authority";
import { ensureCatalogLoaded } from "@/services/ensure-catalog-loaded";
import { previewOrder } from "@/services/preview-order";
import { getResolvedSlug } from "@/config/restaurant-id";
import { primePricingPreviewMemoryCache } from "@/services/prime-pricing-preview-cache";
import { trackEvent } from "@/utils/analytics";
import { getRestaurantId } from "@/config/restaurant-id";
import { WEBSITE_ORDER_CHANNEL } from "@/constants/orderChannel";
import { DELIVERY_ORDER_TYPE } from "@/domain/checkout/fulfilment";
import {
  storefrontBasePathFromPathname,
  storefrontJoin,
} from "@/utils/storefront-paths";

export type ReorderRuntimeFailureCode =
  | ReorderPipelineFailureCode
  | "CATALOG_FETCH_FAILED"
  | "PREVIEW_FAILED";

export type ExecuteReorderParams = {
  order: OrderDetailsUI;
  userId: string;
  firstName: string;
  coupons: { selected?: Discount };
  fallbackMenuItems: MenuItemInterface[];
  defaultAddressId: string;
  /** Effective checkout `order_type` to price the reorder with. Defaults to Delivery (3). */
  orderType?: number;
  getCartItems: () => CartLine[];
  replaceItems: (items: CartLine[]) => void;
  unlockCart: () => void;
  setCheckoutLocked: (locked: boolean) => void;
  isLocked: boolean;
  isCheckoutLocked: boolean;
  syncCatalogAuthority: (authority: CatalogSnapshotAuthority) => void;
  syncMenuItemsWithCounts: (menuItems: MenuItemInterface[]) => void;
  setBillingPreview: (preview: BillingPreviewUI) => void;
  setCheckoutPhase: (phase: "cart") => void;
  navigate: (path: string) => void;
};

export type ExecuteReorderSuccess = {
  status: "success";
  lines: CartLine[];
  summary: ReorderBestEffortSummary;
  presentation: ReorderPresentation;
};

export type ExecuteReorderFailure = {
  status: "failure";
  code: ReorderRuntimeFailureCode;
  failure_code: ReorderRuntimeFailureCode;
  presentation: ReorderPresentation;
};

export type ExecuteReorderResult = ExecuteReorderSuccess | ExecuteReorderFailure;

export type ReorderRuntimeDeps = {
  ensureCatalogLoaded: typeof ensureCatalogLoaded;
  evaluateReorderPipeline: typeof evaluateReorderPipeline;
  previewOrder: typeof previewOrder;
  primePricingPreviewMemoryCache: typeof primePricingPreviewMemoryCache;
  trackEvent: typeof trackEvent;
};

const defaultDeps: ReorderRuntimeDeps = {
  ensureCatalogLoaded,
  evaluateReorderPipeline,
  previewOrder,
  primePricingPreviewMemoryCache,
  trackEvent,
};

function buildRuntimeFailure(
  code: ReorderRuntimeFailureCode,
  presentation: ReorderPresentation
): ExecuteReorderFailure {
  return {
    status: "failure",
    code,
    failure_code: code,
    presentation,
  };
}

export function formatReorderFailureMessage(presentation: ReorderPresentation): string {
  return presentation.headline;
}

/**
 * Authoritative reorder runtime: catalog → pipeline → cart → preview → navigate.
 * Best-effort: commits when at least one line restores; surfaces review notice when partial.
 */
export async function executeReorder(
  params: ExecuteReorderParams,
  deps: Partial<ReorderRuntimeDeps> = {}
): Promise<ExecuteReorderResult> {
  const runtime = { ...defaultDeps, ...deps };
  const orderId = params.order.orderId;
  const priorCart = params.getCartItems();
  let cartCommitted = false;

  const catalogLoad = await runtime.ensureCatalogLoaded(params.order.restaurantId, {
    force: true,
    slug: getResolvedSlug() ?? "",
  });
  if (!catalogLoad.ok || !catalogLoad.catalog.loaded) {
    const presentation = deriveCatalogFetchFailurePresentation();
    runtime.trackEvent(
      "reorder_failure",
      buildReorderFailureAnalyticsPayload(orderId, "CATALOG_FETCH_FAILED")
    );
    return buildRuntimeFailure("CATALOG_FETCH_FAILED", presentation);
  }

  const authority = synchronizeCatalogSnapshotAuthority({
    catalog: catalogLoad.catalog,
    menuItems: catalogLoad.menuItems,
    fallbackMenuItems: params.fallbackMenuItems,
  });
  params.syncCatalogAuthority(authority);

  const pipeline = runtime.evaluateReorderPipeline({
    order: params.order,
    catalog: authority.catalogSnapshot,
  });

  if (pipeline.status === "failure") {
    runtime.trackEvent(
      "reorder_failure",
      buildReorderFailureAnalyticsPayload(orderId, pipeline.code, pipeline.diagnostics)
    );
    return buildRuntimeFailure(pipeline.code, pipeline.presentation);
  }

  const lines = pipeline.lines;

  try {
    if (params.isCheckoutLocked) params.setCheckoutLocked(false);
    if (params.isLocked) params.unlockCart();

    params.replaceItems(lines);
    cartCommitted = true;

    if (shouldSurfaceReorderReviewBanner(pipeline.presentation)) {
      setReorderCartNotice(pipeline.presentation);
    }

    const menuItemsWithCounts = mergeMenuItemCountsFromCartLines(authority.list, lines);
    params.syncMenuItemsWithCounts(menuItemsWithCounts);

    const preview = await runtime.previewOrder(lines, {
      discounts: params.coupons,
      userName: params.firstName,
      orderType: params.orderType ?? DELIVERY_ORDER_TYPE,
      orderChannel: WEBSITE_ORDER_CHANNEL,
      menuItems: menuItemsWithCounts,
      slug: getResolvedSlug() ?? "",
    });

    runtime.primePricingPreviewMemoryCache({
      restaurantId: getRestaurantId(),
      userName: params.firstName,
      billing: preview,
      cartLines: lines,
      discountId: params.coupons?.selected?.discount_id ?? "",
      menuItems: menuItemsWithCounts,
      addressFingerprint: params.defaultAddressId,
      orderType: params.orderType ?? DELIVERY_ORDER_TYPE,
      orderChannel: WEBSITE_ORDER_CHANNEL,
    });

    params.setBillingPreview(preview);
    params.setCheckoutPhase("cart");
    params.navigate(
      storefrontJoin(
        storefrontBasePathFromPathname(window.location.pathname),
        "/checkout"
      )
    );

    runtime.trackEvent("reorder_success", {
      orderId,
      added_count: pipeline.summary.addedItems.length,
      skipped_count: pipeline.summary.skippedItems.length,
      removed_customization_count: pipeline.summary.removedCustomizations.length,
      requires_review: pipeline.summary.requiresReview,
    });
    return {
      status: "success",
      lines,
      summary: pipeline.summary,
      presentation: pipeline.presentation,
    };
  } catch (error: unknown) {
    if (cartCommitted) {
      params.replaceItems(priorCart);
    }

    runtime.trackEvent(
      "reorder_failure",
      buildReorderFailureAnalyticsPayload(orderId, "PREVIEW_FAILED")
    );

    return buildRuntimeFailure("PREVIEW_FAILED", {
      headline: "Couldn't prepare your cart. Please try again.",
      primaryAction: "retry",
    });
  }
}
